import React from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { alpha, Typography } from '@mui/material';

const SummaryDefaultUI = <Typography>Summary</Typography>;
const DetailsDefaultUI = (
    <Typography>
        Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse malesuada lacus ex, sit
        amet blandit leo lobortis eget.
    </Typography>
);

const getRandomNumber = () => Math.random() * 10;

/**
 * CustomAccordion Component
 * @param {Object} props - Component other remaining props
 * @param {JSX.Element} props.summary - The content of the AccordionSummary, default is SummaryDefaultUI
 * @param {JSX.Element} props.details - The content of the AccordionDetails, default is DetailsDefaultUI
 * @param {string} props.component - The root component of the Accordion
 * @param {boolean} props.defaultExpanded - If true, the accordion is expanded by default
 * @param {Object|function} props.sx - Custom styles, can be an object or function that receives the theme
 * @param {JSX.Element} props.expandIcon - Custom expand icon, default is ExpandMoreIcon
 */

const CustomAccordion = ({
    summary = SummaryDefaultUI,
    details = DetailsDefaultUI,
    component,
    defaultExpanded,
    sx = {},

    expandIcon,
    ...props
}) => {
    const index = getRandomNumber();
    return (
        <Accordion
            component={component || 'div'}
            sx={(theme) => ({
                padding: `${theme.typography.generic.xSmall} ${theme.typography.generic.medium}`,

                '&.MuiAccordion-rounded, &.MuiAccordion-rounded:first-of-type': {
                    borderRadius: theme.typography.borderRadius.medium, // "12px",
                },

                '&::before': {
                    display: 'none',
                },

                '&, &.Mui-expanded': {
                    m: 0,
                    mt: '0.5rem',
                },

                '& .MuiAccordionSummary-content': {
                    m: 0,
                    p: 0,
                },
                '& .MuiAccordionSummary-content.Mui-expanded': {
                    m: 0,
                    p: 0,
                },

                '& .MuiAccordionSummary-root, & .MuiAccordionSummary-root.Mui-expanded': {
                    padding: `${theme.typography.generic.none}`,
                    margin: `${theme.typography.generic.none}`,

                    minHeight: '1.875rem',
                },

                '& .MuiAccordionSummary-expandIconWrapper .MuiSvgIcon-root, & .MuiAccordionSummary-expandIconWrapper svg': {
                    color: theme.palette.icon.neutralTertiary,
                },

                '& .MuiAccordionSummary-root.Mui-expanded': {
                    minHeight: '2.375rem',

                    '& .MuiAccordionSummary-expandIconWrapper .MuiSvgIcon-root,& .MuiAccordionSummary-expandIconWrapper svg': {
                        color: theme.palette.action.active,
                    },
                },

                '& .MuiAccordionDetails-root': {
                    padding: `${theme.typography.generic.none}`,

                    margin: `${theme.typography.generic.none}`,

                    background: 'transparent',
                },

                ...(typeof sx === 'function' ? sx(theme) : sx),
            })}
            defaultExpanded={defaultExpanded}
            {...props}
        >
            <AccordionSummary expandIcon={expandIcon || <ExpandMoreIcon />} sx={{ m: 0 }} aria-controls={`panel${index}-content`} id={`panel${index}-header`}>
                {summary}
            </AccordionSummary>

            <AccordionDetails>{details}</AccordionDetails>
        </Accordion>
    );
};

export { CustomAccordion };
